import {
    signal,
    computed,
    type Signal,
    type WritableSignal,
} from '@angular/core';

/** Possible states of a query. */
export const QUERY_STATUS = {
    Idle: 'idle',
    Loading: 'loading',
    Refetching: 'refetching',
    Success: 'success',
    Error: 'error',
} as const;

/**
 * All query states. Use for `liveStatus`; includes `refetching` so you can detect in-flight fetches.
 */
export type QueryLiveStatus = (typeof QUERY_STATUS)[keyof typeof QUERY_STATUS];

/**
 * Query states without `refetching`. Use for `status`; during refetch it stays at `success` or `error`.
 */
export type QueryStatus = Exclude<
    QueryLiveStatus,
    typeof QUERY_STATUS.Refetching
>;

/**
 * Terminal states only (`success` | `error`). Use for outcome APIs (e.g. guards, fetchAsync).
 */
export type QuerySettledStatus = Exclude<
    QueryLiveStatus,
    | typeof QUERY_STATUS.Idle
    | typeof QUERY_STATUS.Loading
    | typeof QUERY_STATUS.Refetching
>;

/** Updates query data: value or `(prev) => newValue`. */
export type DataUpdater<T> = ((prev: T | null) => T | null) | (() => T | null);

/** State and methods for an async data query. */
export interface Query<T, E = unknown> {
    /** Signal emitting the data of the query, or null if not yet loaded or an error occurred. */
    data: Signal<T | null>;
    /** Signal emitting any error encountered during the query. */
    error: Signal<E | null>;
    /** Never emits `refetching`; stays at `success`/`error` during refetch. */
    status: Signal<QueryStatus>;
    /** Emits all states including `refetching`. Filter for `success`/`error` when gating outcomes. */
    liveStatus: Signal<QueryLiveStatus>;

    /** Indicates if query is in its initial idle state */
    isIdle: Signal<boolean>;
    /** Indicates if query is in its initial loading state */
    isLoading: Signal<boolean>; // true during initial load
    /** Indicates if query is refetching data after an initial load */
    isRefetching: Signal<boolean>; // true during follow-up fetch
    /** Indicates if query is currently fetching data (either initial load or refetch) */
    isFetching: Signal<boolean>; // true whenever loading
    /** Indicates if query completed successfully */
    isSuccess: Signal<boolean>;
    /** Indicates if query resulted in an error */
    isError: Signal<boolean>;
    /** Indicates if query has been fetched at least once (successfully or with an error) */
    isFetched: Signal<boolean>;

    /** Sets the query status to loading (if initial) */
    setLoading: () => void;
    /**
     * Sets the data for the query and updates status to success
     * @param newDataOrUpdater - The new data or a function to update the existing data
     */
    setData: (newDataOrUpdater: T | DataUpdater<T>) => void;
    /**
     * Sets the error for the query and updates status to error
     * @param error - The error object
     */
    setError: (error: E) => void;
    /** Resets the query to its initial idle state */
    reset: () => void;
}

/** Mutator keys excluded from `QueryView`. */
export type QueryMutators = 'setLoading' | 'setData' | 'setError' | 'reset';

/** Read-only query; mutators excluded. */
export type QueryView<T, E = unknown> = Omit<Query<T, E>, QueryMutators>;

/** Returns a read-only view of a query. */
export function readQuery<T, E = unknown>(query: Query<T, E>): QueryView<T, E> {
    return query;
}

/** Creates a new query. */
export function createQuery<T, E = unknown>(): Query<T, E> {
    const data: WritableSignal<T | null> = signal(null);
    const error = signal<E | null>(null);
    const status = signal<QueryStatus>(QUERY_STATUS.Idle);

    const fetched = signal(false);
    const loading = signal(false);

    const isIdle = computed(() => status() === QUERY_STATUS.Idle);
    const isLoading = computed(() => loading() && !fetched()); // initial load
    const isRefetching = computed(() => loading() && fetched()); // follow-up
    const isFetching = computed(() => loading()); // any loading

    const liveStatus = computed((): QueryLiveStatus => {
        if (loading()) {
            return fetched() ? QUERY_STATUS.Refetching : QUERY_STATUS.Loading;
        }
        return fetched() ? status() : QUERY_STATUS.Idle;
    });

    const isSuccess = computed(() => status() === QUERY_STATUS.Success);
    const isError = computed(() => status() === QUERY_STATUS.Error);
    const isFetched = computed(() => fetched());

    function setLoading() {
        loading.set(true);
        if (!fetched()) {
            status.set(QUERY_STATUS.Loading);
        }
    }

    function setData(newDataOrUpdater: T | DataUpdater<T>) {
        loading.set(false);
        if (typeof newDataOrUpdater === 'function') {
            data.update(newDataOrUpdater as DataUpdater<T>);
        } else {
            data.set(newDataOrUpdater);
        }
        error.set(null);
        status.set(QUERY_STATUS.Success);
        fetched.set(true);
    }

    function setError(err: E) {
        loading.set(false);
        error.set(err);
        status.set(QUERY_STATUS.Error);
        fetched.set(true);
    }

    function reset() {
        data.set(null);
        error.set(null);
        status.set(QUERY_STATUS.Idle);
        loading.set(false);
        fetched.set(false);
    }

    return {
        data,
        error,
        status,
        liveStatus,
        isIdle,
        isLoading,
        isRefetching,
        isFetching,
        isSuccess,
        isError,
        isFetched,
        setLoading,
        setData,
        setError,
        reset,
    };
}

